import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Space, Tag, Typography } from "antd";
import {
  agentPlatformApi,
  AgentPlatformApiError,
  MonitoringMission,
} from "./api";
import { formatAgentDateTime } from "./dateTime";

const { Text, Title } = Typography;
const newKey = () =>
  `agent-platform-monitoring-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2)}`;
const copy = (error: unknown) =>
  error instanceof AgentPlatformApiError && error.status === 409
    ? "This monitoring state changed. Refresh before trying again."
    : error instanceof Error
    ? error.message
    : "Unable to update monitoring.";

export default function Monitoring({
  workspaceId,
  onCompletedCheck,
}: {
  workspaceId: string;
  onCompletedCheck?: () => void;
}): React.ReactElement | null {
  const [enabled, setEnabled] = useState(false);
  const [mission, setMission] = useState<MonitoringMission | null>(null);
  const [error, setError] = useState<string>();
  const [pending, setPending] = useState(false);
  const version = useRef(0);
  const workspaceEpoch = useRef(0);
  const controller = useRef<AbortController>();
  const retries = useRef(
    new Map<string, { key: string; missionVersion?: number }>()
  );
  const completedCheck = useRef<string>();
  const observedMission = useRef(false);
  const load = useCallback(async () => {
    const current = ++version.current;
    controller.current?.abort();
    const next = new AbortController();
    controller.current = next;
    try {
      const result = await agentPlatformApi.monitoring(
        workspaceId,
        next.signal
      );
      if (current === version.current) {
        const completedKey =
          result.mission?.lastCheckAt && result.mission.lastBriefId
            ? `${result.mission.lastCheckAt}:${result.mission.lastBriefId}`
            : undefined;
        if (
          completedKey &&
          observedMission.current &&
          completedCheck.current !== completedKey
        )
          onCompletedCheck?.();
        if (completedKey) completedCheck.current = completedKey;
        observedMission.current = true;
        setEnabled(result.enabled);
        setMission(result.mission);
        setError(undefined);
      }
    } catch (err) {
      if (current !== version.current || (err as { code?: string }).code === "ERR_CANCELED") return;
      if (err instanceof AgentPlatformApiError && err.status === 404) {
        setEnabled(false);
        setError(undefined);
        return;
      }
      setError(copy(err));
    }
  }, [workspaceId]);
  useEffect(() => {
    workspaceEpoch.current += 1;
    setPending(false);
    setMission(null);
    setEnabled(false);
    setError(undefined);
    retries.current.clear();
    completedCheck.current = undefined;
    observedMission.current = false;
    void load();
    return () => {
      workspaceEpoch.current += 1;
      version.current += 1;
      controller.current?.abort();
    };
  }, [load]);
  useEffect(() => {
    if (!mission) return undefined;
    const id = window.setInterval(() => void load(), 15_000);
    return () => window.clearInterval(id);
  }, [mission, load]);
  const act = async (action: "start" | "pause" | "resume" | "check-now") => {
    if (pending || (action !== "start" && !mission)) return;
    const current = workspaceEpoch.current;
    const saved = retries.current.get(action) || {
      key: newKey(),
      missionVersion: mission?.missionVersion,
    };
    retries.current.set(action, saved);
    setPending(true);
    setError(undefined);
    try {
      const result = await agentPlatformApi.monitoringAction(
        workspaceId,
        action,
        saved.key,
        saved.missionVersion
      );
      if (current === workspaceEpoch.current) {
        retries.current.delete(action);
        setMission(result.mission);
      }
    } catch (err) {
      if (err instanceof AgentPlatformApiError && err.status === 409)
        retries.current.delete(action);
      if (current === workspaceEpoch.current) setError(copy(err));
    } finally {
      if (current === workspaceEpoch.current) setPending(false);
    }
  };
  if (!enabled && !error) return null;
  const stale =
    mission?.heartbeatAt &&
    Date.now() - new Date(mission.heartbeatAt).getTime() > 30_000;
  const statusLabel =
    mission?.status === "waiting_quota"
      ? "Waiting for quota"
      : mission?.status === "stopped_reconciliation"
      ? "Needs review"
      : mission?.status;
  return (
    <Card
      className="agent-platform-monitoring"
      title={<Title level={3}>Monitoring</Title>}
      extra={
        <Button onClick={() => void load()} disabled={pending}>
          Refresh
        </Button>
      }
    >
      <Space direction="vertical" className="agent-platform-full">
        <Text type="secondary">
          Runs only in the local process while it is available. Manual checks
          remain available in Briefs.
        </Text>
        {error && (
          <Alert
            type="error"
            message={error}
            action={
              <Button size="small" onClick={() => void load()}>
                Refresh
              </Button>
            }
          />
        )}
        {mission ? (
          <>
            <Tag>{statusLabel}</Tag>
            {!mission.heartbeatAt && (
              <Alert type="info" message="Waiting for the local worker" />
            )}
            {stale && (
              <Alert
                type="warning"
                message={
                  mission.status === "active"
                    ? "Local monitoring appears offline."
                    : "The local worker heartbeat is stale."
                }
              />
            )}
            {mission.status === "waiting_quota" && (
              <Alert type="info" message="Waiting for the next quota window." />
            )}
            {mission.status === "stopped_reconciliation" && (
              <Alert
                type="warning"
                message="Monitoring stopped for reconciliation. Review the saved brief before starting again."
              />
            )}
            {mission.pauseReason && (
              <Text type="secondary">
                Reason: {mission.pauseReason.replace("_", " ")}
              </Text>
            )}
            <Text>Last check: {formatAgentDateTime(mission.lastCheckAt) || "Not yet checked"}</Text>
            <Text>Next check: {formatAgentDateTime(mission.nextCheckAt) || "Time unavailable"}</Text>
            {mission.status === "active" ||
            mission.status === "waiting_quota" ? (
              <Button onClick={() => void act("pause")} disabled={pending}>
                Pause
              </Button>
            ) : mission.status === "paused" ? (
              <Button onClick={() => void act("resume")} disabled={pending}>
                Resume
              </Button>
            ) : mission.status === "stopped_reconciliation" ? null : (
              <Button onClick={() => void act("start")} disabled={pending}>
                Start monitoring
              </Button>
            )}
            <Button
              onClick={() => void act("check-now")}
              disabled={pending || mission.status !== "active"}
            >
              Check now
            </Button>
          </>
        ) : (
          <Button
            type="primary"
            onClick={() => void act("start")}
            disabled={pending}
          >
            Start monitoring
          </Button>
        )}
      </Space>
    </Card>
  );
}
